import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fmtData as fmtDate, fmtMoeda } from "../lib";

// M1: helpers centralizados em ../lib (fmtData/fmtMoeda)
const fmtBRL = fmtMoeda;

const RESP_LABEL = { camasa: "CAMASA", cliente: "Cliente", terceiro: "Terceiro" };

export default function RetrabalhosDashboard({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({ data_inicio: "", data_fim: "", responsavel_custo: "", prestador_id: "" });
  const [sortKey, setSortKey] = useState("data");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const qs = params.toString();
      const res = await api("get", `/dashboard/retrabalhos${qs ? "?" + qs : ""}`);
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao carregar dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => load();
  const clearFilters = () => { setFilters({ data_inicio: "", data_fim: "", responsavel_custo: "", prestador_id: "" }); setTimeout(load, 0); };

  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    const arr = [...data.items];
    arr.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === "custo") { av = Number(av) || 0; bv = Number(bv) || 0; }
      else { av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const sortIcon = (k) => sortKey !== k ? "↕" : (sortDir === "asc" ? "↑" : "↓");

  // Lista de prestadores únicos pra filtro
  const prestadores = useMemo(() => {
    const m = new Map();
    (data?.items || []).forEach(i => { if (i.prestador_id) m.set(i.prestador_id, i.prestador_nome || "—"); });
    return [...m.entries()].map(([id, nome]) => ({ id, nome }));
  }, [data]);

  if (loading && !data) {
    return <div className="fade-up" style={{ padding: 32, color: "#cdc8be" }} data-testid="retrabalhos-dash-loading">Carregando dashboard de retrabalhos…</div>;
  }
  if (err) {
    return <div className="fade-up" style={{ padding: 32, color: "#c47a7a" }} data-testid="retrabalhos-dash-err">⚠ {err}</div>;
  }
  const k = data?.kpis || { total: 0, custo_total: 0, custo_camasa: 0, custo_cliente: 0, custo_terceiro: 0 };

  return (
    <div className="fade-up" data-testid="retrabalhos-dashboard" style={{ padding: "24px 32px 64px", maxWidth: 1400, margin: "0 auto" }}>
      <div className="ws-head" style={{ marginBottom: 24 }}>
        <div>
          <div className="eyebrow" style={{ color: "#c47a7a" }}>🔧 QUALIDADE & PROCESSO</div>
          <h1>Retrabalhos <em>por responsável</em></h1>
          <div style={{ color: "#8a857a", fontSize: 13, marginTop: 6, maxWidth: 640 }}>
            Visão consolidada de todos os retrabalhos registrados nas obras. Permite identificar padrões, prestadores problemáticos e custos absorvidos.
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 28 }} data-testid="retrabalhos-kpis">
        <KpiCard label="Total de retrabalhos" value={k.total} accent="#c47a7a" testid="kpi-total" />
        <KpiCard label="Custo absorvido — CAMASA" value={fmtBRL(k.custo_camasa)} accent="#b8534a" testid="kpi-camasa" />
        <KpiCard label="Cobrado de Clientes" value={fmtBRL(k.custo_cliente)} accent="#b8935a" testid="kpi-cliente" />
        <KpiCard label="Cobrado de Terceiros" value={fmtBRL(k.custo_terceiro)} accent="#5a7a6a" testid="kpi-terceiro" />
      </div>

      {/* Filtros */}
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", padding: 16, marginBottom: 24 }} data-testid="retrabalhos-filtros">
        <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "#8a857a", marginBottom: 12 }}>Filtros</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
          <div className="field">
            <label>Data início</label>
            <input type="date" className="input" value={filters.data_inicio}
              onChange={e => setFilters({ ...filters, data_inicio: e.target.value })}
              data-testid="filter-data-inicio" />
          </div>
          <div className="field">
            <label>Data fim</label>
            <input type="date" className="input" value={filters.data_fim}
              onChange={e => setFilters({ ...filters, data_fim: e.target.value })}
              data-testid="filter-data-fim" />
          </div>
          <div className="field">
            <label>Responsável pelo custo</label>
            <select className="input" value={filters.responsavel_custo}
              onChange={e => setFilters({ ...filters, responsavel_custo: e.target.value })}
              data-testid="filter-resp">
              <option value="">Todos</option>
              <option value="camasa">CAMASA</option>
              <option value="cliente">Cliente</option>
              <option value="terceiro">Terceiro</option>
            </select>
          </div>
          <div className="field">
            <label>Prestador</label>
            <select className="input" value={filters.prestador_id}
              onChange={e => setFilters({ ...filters, prestador_id: e.target.value })}
              data-testid="filter-prestador">
              <option value="">Todos</option>
              {prestadores.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={applyFilters} data-testid="filter-apply">Filtrar</button>
            <button className="btn btn-ghost btn-sm" onClick={clearFilters} data-testid="filter-clear">Limpar</button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ marginBottom: 36 }}>
        <h3 style={{ fontSize: 14, letterSpacing: ".15em", textTransform: "uppercase", color: "#cdc8be", marginBottom: 12 }}>
          Retrabalhos registrados ({sortedItems.length})
        </h3>
        {sortedItems.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#8a857a", border: "1px dashed rgba(255,255,255,0.1)" }} data-testid="retrabalhos-empty">
            Nenhum retrabalho encontrado com os filtros aplicados.
          </div>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid rgba(255,255,255,0.08)" }}>
            <table className="table" style={{ minWidth: 1000, fontSize: 12 }} data-testid="retrabalhos-table">
              <thead>
                <tr>
                  <Th onClick={() => toggleSort("obra_codigo")}>Obra {sortIcon("obra_codigo")}</Th>
                  <Th onClick={() => toggleSort("nome")}>Etapa {sortIcon("nome")}</Th>
                  <Th onClick={() => toggleSort("motivo")}>Motivo {sortIcon("motivo")}</Th>
                  <Th onClick={() => toggleSort("responsavel_custo")}>Responsável {sortIcon("responsavel_custo")}</Th>
                  <Th onClick={() => toggleSort("prestador_nome")}>Prestador {sortIcon("prestador_nome")}</Th>
                  <Th onClick={() => toggleSort("custo")} style={{ textAlign: "right" }}>Custo {sortIcon("custo")}</Th>
                  <Th onClick={() => toggleSort("data")}>Data {sortIcon("data")}</Th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((i, idx) => (
                  <tr key={idx} data-testid={`retrabalho-row-${idx}`}>
                    <td>
                      <Link to={`/obras/${i.obra_id}`} style={{ color: "#b8935a", textDecoration: "none" }}>
                        {i.obra_codigo || "—"}
                      </Link>
                      <div style={{ fontSize: 10, color: "#8a857a" }}>{i.obra_nome}</div>
                    </td>
                    <td style={{ color: "#c47a7a", fontWeight: 600 }}>{i.nome}</td>
                    <td style={{ maxWidth: 280, color: "#cdc8be" }}>{i.motivo || "—"}</td>
                    <td>
                      <span style={{
                        fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
                        padding: "2px 8px", border: "1px solid rgba(196,122,122,0.4)",
                        color: "#c47a7a", background: "rgba(196,122,122,0.08)",
                      }}>
                        {RESP_LABEL[i.responsavel_custo] || i.responsavel_custo}
                      </span>
                    </td>
                    <td>{i.prestador_nome || <span style={{ color: "#5a554a" }}>—</span>}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {i.custo > 0 ? fmtBRL(i.custo) : <span style={{ color: "#5a554a" }}>—</span>}
                    </td>
                    <td>{fmtDate(i.data)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rankings */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 24 }}>
        <RankingCard
          title="Prestadores com mais retrabalhos"
          subtitle="Use isso para renegociar contratos ou aplicar SLA"
          items={data?.ranking_prestadores || []}
          renderRow={(r, i) => (
            <tr key={r.prestador_id} data-testid={`rank-prest-${i}`}>
              <td style={{ width: 24, color: "#b8935a", fontWeight: 600 }}>{i + 1}º</td>
              <td>{r.prestador_nome}</td>
              <td style={{ textAlign: "center", fontWeight: 600 }}>{r.qtd}</td>
              <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtBRL(r.custo_total)}</td>
            </tr>
          )}
          headers={["#", "Prestador", "Qtd", "Custo Total"]}
          emptyMsg="Nenhum prestador associado a retrabalhos ainda"
          testid="ranking-prestadores"
        />
        <RankingCard
          title="Obras com mais retrabalhos"
          subtitle="Investigue padrões: arquiteto, prestador, complexidade"
          items={data?.ranking_obras || []}
          renderRow={(r, i) => (
            <tr key={r.obra_id} data-testid={`rank-obra-${i}`}>
              <td style={{ width: 24, color: "#b8935a", fontWeight: 600 }}>{i + 1}º</td>
              <td>
                <Link to={`/obras/${r.obra_id}`} style={{ color: "#cdc8be", textDecoration: "none" }}>
                  <strong style={{ color: "#b8935a" }}>{r.codigo}</strong> · {r.nome}
                </Link>
              </td>
              <td style={{ textAlign: "center", fontWeight: 600 }}>{r.qtd}</td>
              <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtBRL(r.custo_total)}</td>
            </tr>
          )}
          headers={["#", "Obra", "Qtd", "Custo Total"]}
          emptyMsg="Nenhuma obra com retrabalhos registrados"
          testid="ranking-obras"
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: "18px 20px",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderTop: `2px solid ${accent}`,
    }}>
      <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "#8a857a", marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: accent, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, onClick, style }) {
  return (
    <th onClick={onClick} style={{
      cursor: "pointer", userSelect: "none",
      fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase",
      color: "#8a857a", padding: "10px 12px", textAlign: "left",
      borderBottom: "1px solid rgba(255,255,255,0.1)", whiteSpace: "nowrap",
      ...style,
    }}>{children}</th>
  );
}

function RankingCard({ title, subtitle, items, renderRow, headers, emptyMsg, testid }) {
  return (
    <div data-testid={testid} style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <h3 style={{ fontSize: 13, letterSpacing: ".12em", textTransform: "uppercase", color: "#cdc8be", margin: 0 }}>{title}</h3>
        <div style={{ fontSize: 11, color: "#8a857a", marginTop: 4 }}>{subtitle}</div>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: 24, color: "#8a857a", fontSize: 12, textAlign: "center" }}>{emptyMsg}</div>
      ) : (
        <table className="table" style={{ fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} style={{
                  fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase",
                  color: "#8a857a", padding: "8px 12px",
                  textAlign: i >= headers.length - 2 ? (i === headers.length - 1 ? "right" : "center") : "left",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{items.map((r, i) => renderRow(r, i))}</tbody>
        </table>
      )}
    </div>
  );
}
